import { useEffect, useState } from 'react';
import landingPageService, { LandingKind, LandingLang, LandingPageSummary } from '../services/landingPageService';
import { currentLang } from '../i18n';

// ============================================
// useLandingPages (2026-09-20) – veröffentlichte Landingpages für
// Header-Dropdown „Lösungen“, Startseiten-Teaser und Hub. EIN Request pro
// Sprache und Seitenaufruf (Modul-Cache mit geteiltem Promise, Muster
// usePlacesTop). Fehler → [] (Konsumenten blenden sich aus).
// ============================================

// 2026-10-26: Cache je Sprache UND Art (Lösungen | Ratgeber)
type CacheKey = `${LandingLang}:${LandingKind}`;
const cache: Partial<Record<CacheKey, LandingPageSummary[]>> = {};
const inflight: Partial<Record<CacheKey, Promise<LandingPageSummary[]>>> = {};
const keyOf = (lang: LandingLang, kind: LandingKind): CacheKey => `${lang}:${kind}`;

export const loadLandingPages = (lang: LandingLang, kind: LandingKind = 'solution'): Promise<LandingPageSummary[]> => {
  const key = keyOf(lang, kind);
  const cached = cache[key];
  if (cached) return Promise.resolve(cached);
  let pending = inflight[key];
  if (!pending) {
    pending = landingPageService
      .getPages(lang, null, kind)
      .then((pages) => {
        cache[key] = Array.isArray(pages) ? pages : [];
        return cache[key]!;
      })
      .catch(() => {
        cache[key] = [];
        return cache[key]!;
      })
      .finally(() => {
        delete inflight[key];
      });
    inflight[key] = pending;
  }
  return pending;
};

/** Nur für Tests: Cache zurücksetzen */
export const resetLandingPagesCache = (): void => {
  for (const l of Object.keys(cache) as CacheKey[]) delete cache[l];
  for (const l of Object.keys(inflight) as CacheKey[]) delete inflight[l];
};

export interface LandingPagesState {
  pages: LandingPageSummary[];
  loading: boolean;
}

/** Veröffentlichte Landingpages in der gewünschten Sprache (Default: App-Sprache). */
export default function useLandingPages(lang?: LandingLang, kind: LandingKind = 'solution'): LandingPagesState {
  const effectiveLang: LandingLang = lang ?? currentLang();
  const key = keyOf(effectiveLang, kind);
  const [pages, setPages] = useState<LandingPageSummary[]>(() => cache[key] ?? []);
  const [loading, setLoading] = useState<boolean>(() => cache[key] === undefined);

  useEffect(() => {
    let cancelled = false;
    const cached = cache[key];
    if (cached) {
      setPages(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    loadLandingPages(effectiveLang, kind).then((list) => {
      if (cancelled) return;
      setPages(list);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveLang, kind, key]);

  return { pages, loading };
}
